/**
 * Server mode: what signs say and item frames hold. The server replicates each
 * as an entity of kind 'be' with the record
 *   [kind 'sign' | 'frame', x, y, z, block id, lines | framed stack, rotation]
 * (server/actions/utility.ts). This draws the text / the item on the block the
 * chunk mesh already shows, and asks for a sign's text when the server says
 * this player is to write it (`fx` k = 'sign.edit').
 */
import * as THREE from 'three';
import { FACE_DIRS } from '../blocks';
import { UK, U_A, U_B, U_KIND } from '../game/utility/defs';
import { drawItemIcon } from '../items';
import type { ClientMsg, ServerMsg } from '../server/protocol';

interface Deps {
  scene: THREE.Scene;
  others(kind: string): { id: number; record: unknown }[];
  /** This player's entity id. */
  self(): number;
  send(msg: ClientMsg): void;
  onFx(handler: (m: Extract<ServerMsg, { t: 'fx' }>) => void): void;
}

const SIGN_LINES = 4, SIGN_CHARS = 15;

interface Drawn { mesh: THREE.Mesh; tex: THREE.CanvasTexture; key: string }

export class TileDrawer {
  private readonly drawn = new Map<number, Drawn>();
  private readonly geo = new THREE.PlaneGeometry(1, 1);
  private editing: HTMLElement | null = null;

  constructor(private readonly d: Deps) {
    d.onFx((m) => {
      const v = m.d as { x?: number; y?: number; z?: number; to?: number; lines?: string[] } | null;
      if (m.k !== 'sign.edit' || !v || v.to !== d.self()) return;
      this.edit(Number(v.x), Number(v.y), Number(v.z), Array.isArray(v.lines) ? v.lines : []);
    });
  }

  /** Once a frame: make the drawn set equal the replicated one. */
  frame(): void {
    const seen = new Set<number>();
    for (const o of this.d.others('be')) {
      const r = o.record;
      if (!Array.isArray(r)) continue;
      seen.add(o.id);
      const key = JSON.stringify(r);
      const had = this.drawn.get(o.id);
      if (had?.key === key) continue;
      if (had) this.remove(o.id, had);
      const made = r[0] === 'sign' ? this.sign(r) : this.framed(r);
      if (made) { made.key = key; this.drawn.set(o.id, made); this.d.scene.add(made.mesh); }
    }
    for (const [id, v] of this.drawn) if (!seen.has(id)) this.remove(id, v);
  }

  private remove(id: number, v: Drawn): void {
    this.d.scene.remove(v.mesh);
    v.tex.dispose();
    (v.mesh.material as THREE.Material).dispose();
    this.drawn.delete(id);
  }

  private plane(canvas: HTMLCanvasElement, w: number, h: number): Drawn {
    const tex = new THREE.CanvasTexture(canvas);
    tex.magFilter = THREE.NearestFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    const mesh = new THREE.Mesh(this.geo, new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.1, polygonOffset: true, polygonOffsetFactor: -1 }));
    mesh.scale.set(w, h, 1);
    return { mesh, tex, key: '' };
  }

  private sign(r: unknown[]): Drawn | null {
    const x = Number(r[1]), y = Number(r[2]), z = Number(r[3]), id = Number(r[4]);
    const lines = Array.isArray(r[5]) ? r[5].map(String) : [];
    const kind = U_KIND[id];
    if ((kind !== UK.SIGN && kind !== UK.WALL_SIGN) || !lines.some((l) => l)) return null;
    const canvas = document.createElement('canvas');
    canvas.width = 384; canvas.height = 192;
    const g = canvas.getContext('2d')!;
    g.fillStyle = '#000';
    g.font = '36px monospace';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    for (let i = 0; i < SIGN_LINES; i++) g.fillText((lines[i] ?? '').slice(0, SIGN_CHARS), 192, 28 + i * 45, 360);
    // the board of entityModels.ts signQuads: 1 x 0.5 blocks, 1/24 either side of its middle
    const wall = kind === UK.WALL_SIGN;
    const a = wall ? U_B[id] * Math.PI / 2 : U_B[id] * Math.PI / 8;
    const out = (wall ? -0.4375 : 0) + 1 / 24 + 0.004;
    const v = this.plane(canvas, 0.94, 0.47);
    v.mesh.position.set(x + 0.5 + Math.sin(a) * out, y + (wall ? 0.5208 : 0.8333), z + 0.5 + Math.cos(a) * out);
    v.mesh.rotation.y = a;
    return v;
  }

  private framed(r: unknown[]): Drawn | null {
    const x = Number(r[1]), y = Number(r[2]), z = Number(r[3]), id = Number(r[4]);
    const s = r[5] as { id?: number } | null;
    if (U_KIND[id] !== UK.FRAME || !s || !s.id) return null;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 64;
    drawItemIcon(canvas.getContext('2d')!, s.id, 64);
    const [dx, dy, dz] = FACE_DIRS[U_A[id]]; // towards the block the frame hangs on
    const v = this.plane(canvas, 0.5, 0.5);
    const off = 0.5 - 1 / 16 - 0.01;
    v.mesh.position.set(x + 0.5 + dx * off, y + 0.5 + dy * off, z + 0.5 + dz * off);
    v.mesh.lookAt(v.mesh.position.x - dx, v.mesh.position.y - dy, v.mesh.position.z - dz);
    v.mesh.rotateZ(-(Number(r[6]) || 0) * Math.PI / 4);
    return v;
  }

  /** The sign edit screen: four lines; Enter moves on, Escape or Done sends. */
  private edit(x: number, y: number, z: number, lines: string[]): void {
    this.editing?.remove();
    if (document.pointerLockElement) document.exitPointerLock();
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;inset:0;z-index:9000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;background:rgba(0,0,0,0.55);font:16px monospace;color:#fff';
    const title = document.createElement('div');
    title.textContent = 'Edit sign message';
    el.appendChild(title);
    const inputs: HTMLInputElement[] = [];
    const done = (): void => {
      if (this.editing !== el) return;
      this.editing = null;
      el.remove();
      this.d.send({ t: 'sign', x, y, z, lines: inputs.map((i) => i.value) });
    };
    for (let i = 0; i < SIGN_LINES; i++) {
      const inp = document.createElement('input');
      inp.maxLength = SIGN_CHARS;
      inp.value = (lines[i] ?? '').slice(0, SIGN_CHARS);
      inp.style.cssText = 'width:240px;font:18px monospace;text-align:center;background:#b8945f;color:#000;border:2px solid #5a4526;padding:2px';
      inp.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); if (i === SIGN_LINES - 1 && e.key === 'Enter') done(); else inputs[Math.min(SIGN_LINES - 1, i + 1)].focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); inputs[Math.max(0, i - 1)].focus(); }
        else if (e.key === 'Escape') { e.preventDefault(); done(); }
      });
      inp.addEventListener('keyup', (e) => e.stopPropagation());
      inp.addEventListener('keypress', (e) => e.stopPropagation());
      inputs.push(inp);
      el.appendChild(inp);
    }
    const btn = document.createElement('button');
    btn.textContent = 'Done';
    btn.style.cssText = 'margin-top:8px;font:16px monospace;padding:4px 24px';
    btn.addEventListener('click', done);
    el.appendChild(btn);
    for (const t of ['mousedown', 'mouseup', 'click', 'wheel', 'contextmenu']) el.addEventListener(t, (e) => e.stopPropagation());
    document.body.appendChild(el);
    this.editing = el;
    inputs[0].focus();
  }
}
